"use client";

import Link from "next/link";

import type { TaggedProduct } from "../../_types/content";
import StorefrontMedia from "./StorefrontMedia";
import { Action, TagList } from "./ui/Primitives";
import { CardTemplate } from "./ui/Templates";

interface ProductCardProps {
  product: TaggedProduct;
  presentation?: "catalog" | "carousel" | "compact";
}

export default function ProductCard({
  product,
  presentation = "catalog",
}: ProductCardProps) {
  const isCarousel = presentation === "carousel";
  const isCompact = presentation === "compact";

  return (
    <CardTemplate
      presentation={presentation}
      bordered
      media={
        <Link
        href={`/test/products/${product.slug}`}
        aria-label={`View ${product.name}`}
        className="block h-full overflow-hidden bg-clay"
      >
        <StorefrontMedia
          image={product.images[0]}
          sizes={
            isCarousel
              ? "(min-width: 1024px) 24vw, (min-width: 640px) 37vw, 71vw"
              : isCompact
                ? "(min-width: 1024px) 14rem, 45vw"
              : "(min-width: 1536px) 16vw, (min-width: 1280px) 20vw, (min-width: 640px) 30vw, 100vw"
          }
          className="transition-transform duration-500 hover:scale-[1.02]"
        />
        </Link>
      }
    >
        <h3
          className={`${isCarousel ? "text-fluid-base" : "text-xl"} ${isCompact ? "line-clamp-2" : ""} font-neueheitz leading-[1.05]`}
        >
          <Link
            href={`/test/products/${product.slug}`}
            className="hover:underline hover:underline-offset-4"
          >
            {product.name}
          </Link>
        </h3>
        {!isCompact ? (
          <p className={`${isCarousel ? "text-base" : "text-sm"} pt-2 leading-snug text-earth`}>
            {product.shortDescription}
          </p>
        ) : null}

        <TagList
          items={product.tagLabels}
          limit={3}
          label="Product grains"
          className={isCarousel ? "pt-4 text-sm" : "pt-3 text-xs [&_span]:px-2 [&_span]:py-0.5"}
        />

        <div className={`mt-auto ${isCarousel ? "pt-6" : "pt-4"}`}>
          <div className={`grid grid-cols-2 ${isCarousel ? "gap-3 pt-4" : "gap-2 pt-3"}`}>
            <Action
              href={product.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              size={isCarousel ? "medium" : "small"}
              className="text-center"
            >
              Shop now
            </Action>
            <Action
              href={`/test/products/${product.slug}`}
              variant="secondary"
              size={isCarousel ? "medium" : "small"}
              className="text-center"
            >
              View
            </Action>
          </div>
        </div>
    </CardTemplate>
  );
}
